/** Path to a node in the tree (child indices from root). */
export type NodePath = number[];

export type GroupOperator = 'AND' | 'OR';

export type ConditionKind =
  | 'installedApp'
  | 'boughtProduct'
  | 'receivedNotification'
  | 'clickedCampaign'
  | 'rfmGroup'
  | 'lastNotificationAt'
  | 'lastAppAccessAt'
  | 'viewedProduct'
  | 'location'
  | 'lastOrder'
  | 'orderFrequency'
  | 'totalSpend'
  | 'hasNotificationsEnabled';

export type DateUnit = 'days' | 'weeks' | 'months' | 'years';

export type DateBound =
  | { type: 'relative'; offset: number; unit: DateUnit; timezone: string }
  | { type: 'absolute'; date: string }
  | { type: 'epoch' };

export type DateFilter =
  | { type: 'relative'; direction: 'before' | 'after'; date: DateBound }
  | { type: 'absolute'; direction: 'before' | 'after'; date: string }
  | { type: 'window'; from: DateBound; to: DateBound }
  | { type: 'anytime' };

export type MatchMode = 'all' | 'any';

export type MatchListValue = {
  match: MatchMode;
  ids: string[];
};

export type NumberPredicate =
  | { kind: 'exact'; value: number }
  | { kind: 'atLeast'; value: number }
  | { kind: 'atMost'; value: number };

export type ConditionArgsByKind = {
  installedApp: { when: DateFilter };
  boughtProduct: { products: MatchListValue; when: DateFilter };
  receivedNotification: { campaigns: MatchListValue };
  clickedCampaign: { campaigns: MatchListValue };
  rfmGroup: { groups: MatchListValue };
  lastNotificationAt: { when: DateFilter };
  lastAppAccessAt: { when: DateFilter };
  viewedProduct: { products: MatchListValue; when: DateFilter };
  location: { locations: MatchListValue };
  lastOrder: { when: DateFilter };
  orderFrequency: { count: NumberPredicate; when: DateFilter };
  totalSpend: { amount: NumberPredicate };
  hasNotificationsEnabled: Record<string, never>;
};

export type TreeConditionNode = {
  [K in ConditionKind]: {
    id: string;
    type: 'condition';
    negated: boolean;
    kind: K;
    args: ConditionArgsByKind[K];
  };
}[ConditionKind];

export type TreeGroupNode = {
  id: string;
  type: 'group';
  operator: GroupOperator;
  negated: boolean;
  children: TreeNode[];
};

export type TreeNode = TreeGroupNode | TreeConditionNode;

export type TreeRoot = TreeGroupNode & { id: 'root' };

export function isGroupNode(node: TreeNode): node is TreeGroupNode {
  return node.type === 'group';
}

export function canAcceptChildren(
  node: TreeNode | null
): node is TreeGroupNode {
  return node !== null && node.type === 'group';
}

export function isConditionNode(node: TreeNode): node is TreeConditionNode {
  return node.type === 'condition';
}
